import React from 'react'
import { useHistory } from 'react-router-dom'

import { Formik } from 'formik'

import { ZariContext } from '_zari/context'

import { Form, Col, Row, Divider, Button } from '_zari/core'

import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'
import TiposDeDocumentoSunatConstant from 'constants/tiposDeDocumentoSunat'
import ModalidadGuiasVentas from 'constants/tiposModalidadGuiaVenta'

import { IdGen, RounderUtil } from 'utils'

import * as GuiasVentaService from 'services/guiasVenta.service'

import * as FormGroups from './formGroups'
import * as Sections from './sections'

import ValidationSchema from './validation'

const actualizarFila = (current, { cantidad, precio, ...restPayload }) => {
  const subTotal = parseFloat(cantidad || '0') * parseFloat(precio || '0')

  current = {
    ...current,
    ...restPayload,
    cantidad,
    precio,
    subTotal
  }

  return current
}

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      let newState = state
        .map(val => ({
          ...val,
          __filaActiva: false,
          __focusCantidad: false,
          esFilaBusqueda: false
        }))
        .filter(x => x._idProductoPresentacion)

      let { nuevaFila, viaCodBar, ...payload } = action.payload

      return [
        ...newState,
        {
          ...payload,
          __filaActiva: !viaCodBar,
          __focusCantidad: !viaCodBar,
          _idFila: IdGen.newId(),
          cantidad: '',
          precio: ''
        },
        {
          __filaActiva: viaCodBar,
          esFilaBusqueda: true,
          _idFila: `${IdGen.newId()}-G`
        }
      ]
    }
    case 'actualizar': {
      const { saltarFila, ...nuevoPayload } = action.payload
      return state.map(val => {
        if (val.esFilaBusqueda) {
          val.__filaActiva = saltarFila
        } else {
          if (val._idFila === nuevoPayload._idFila) {
            val = actualizarFila(val, nuevoPayload)
          }
          val.__filaActiva = !saltarFila
        }
        return val
      })
    }
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    default:
      return state
  }
}

const NuevaMainForm = ({ editarData }) => {
  const __zariContext = React.useContext(ZariContext)

  const history = useHistory()

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, editarData.detalles, inputArr => {
    if (inputArr) {
      const salida = inputArr.map(x => {
        return {
          ...x,
          _idFila: IdGen.newId()
        }
      })

      return [
        ...salida,
        {
          esFilaBusqueda: true,
          _idFila: IdGen.newId()
        }
      ]
    }

    return [
      {
        esFilaBusqueda: true,
        _idFila: IdGen.newId()
      }
    ]
  })

  const mainFormikRef = React.useRef({})

  const formInitialValues = React.useMemo(
    () => ({
      _idConductor: editarData._idConductor ?? '',
      _idDireccion: editarData._idDireccion ?? '',
      _idDocumentoRelacionado: editarData._idDocumentoRelacionado ?? '',
      _idPlaca: editarData._idPlaca ?? '',
      _idSocio: editarData._idSocio ?? '',
      _idTransportista: editarData._idTransportista ?? '',
      codigoMotivo: editarData.codigoMotivo ?? '',
      comentario: editarData.comentario ?? '',
      documentoSocio: editarData.documentoSocio ?? '',
      fechaEmision: editarData.fechaEmision ?? '',
      fechaEntrega: editarData.fechaEntrega ?? '',
      fechaTraslado: editarData.fechaTraslado ?? '',
      modalidad: editarData.modalidad ?? '',
      nombreSocio: editarData.nombreSocio ?? '',
      numeroDocumento: editarData.numeroDocumento ?? '',
      serieDocumento: editarData.serieDocumento ?? '',
      tipoDocumento: editarData.tipoDocumento ?? TiposDeDocumentoSunatConstant.GUIA_REMISION_REMITENTE,
      tipoDocumentoSocio: editarData.tipoDocumentoSocio ?? ''
    }),
    [editarData]
  )

  const sumaDetalles = React.useMemo(() => {
    const detallesValidos = detalles.filter(x => !x.esFilaBusqueda)
    const suma = detallesValidos.reduce(
      (sum, current) => RounderUtil.round(sum + parseFloat(current.subTotal || '0')),
      0
    )
    return suma
  }, [detalles])

  const submitHandler = (data, { setSubmitting }) => {
    const detallesNormalizados = detalles
      .filter(x => !x.esFilaBusqueda)
      .map(x => {
        return {
          _idProductoPresentacion: x._idProductoPresentacion,
          cantidad: parseFloat(x.cantidad),
          precio: parseFloat(x.precio),
          subTotal: parseFloat(x.subTotal)
        }
      })

    if (!detallesNormalizados || detallesNormalizados.length === 0) {
      __zariContext.snack('warning', 'Ingresar detalles.')
      return
    }

    if (editarData._id) {
      GuiasVentaService.actualizarGuia(editarData._id, {
        ...data,
        detalles: detallesNormalizados
      })
        .then(() => {
          __zariContext.snack('success', 'El registro se ha actualizado.')
          history.push(`/${CodigosTransaccionesConstant.VENTASINGRESOGUIA_LISTA}`)
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          setSubmitting(false)
        })
    } else {
      GuiasVentaService.nuevaGuia({
        ...data,
        detalles: detallesNormalizados
      })
        .then(() => {
          __zariContext.snack('success', 'El registro se ha agregado.')
          history.push(`/${CodigosTransaccionesConstant.VENTASINGRESOGUIA_LISTA}`)
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          setSubmitting(false)
        })
    }
  }

  return (
    <>
      <Row>
        <Col md={12}>
          <Formik
            initialValues={formInitialValues}
            onSubmit={submitHandler}
            innerRef={mainFormikRef}
            validationSchema={ValidationSchema}
          >
            {({ errors, isSubmitting, setValues, touched, values, handleChange, handleSubmit, setFieldValue }) => (
              <>
                <Form>
                  <Row>
                    <Col md={4}>
                      <FormGroups.SocioFormGroup iValue={values._idSocio} values={values} setValues={setValues} />
                      {values._idSocio !== '' && (
                        <>
                          <FormGroups.DireccionFormGroup
                            iValue_IdSocio={values._idSocio}
                            iError={errors._idDireccion}
                            iTouched={touched._idDireccion}
                            fIsSubmitting={isSubmitting}
                            setFieldValue={setFieldValue}
                          />
                        </>
                      )}
                      <FormGroups.TipoDocumentoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.tipoDocumento}
                        iTouched={touched.tipoDocumento}
                        handleChange={handleChange}
                        setValues={setValues}
                        values={values}
                      />
                      <FormGroups.SerieNumeroFormGroup
                        fIsSubmitting={isSubmitting}
                        setFieldValue={setFieldValue}
                        tipoDocumentoValue={values.tipoDocumento}
                      />
                    </Col>
                    <Col md={4}>
                      <FormGroups.MotivoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.codigoMotivo}
                        iTouched={touched.codigoMotivo}
                      />
                      <FormGroups.FechaEmisionFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.fechaEmision}
                        iTouched={touched.fechaEmision}
                      />
                      <FormGroups.FechaEntregaFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.fechaEntrega}
                        iTouched={touched.fechaEntrega}
                      />
                      <FormGroups.FechaTrasladoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.fechaTraslado}
                        iTouched={touched.fechaTraslado}
                      />
                    </Col>
                    <Col md={4}>
                      <FormGroups.ModalidadFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.modalidad}
                        iTouched={touched.modalidad}
                        handleChange={handleChange}
                        values={values}
                        setValues={setValues}
                      />
                      {values.modalidad === ModalidadGuiasVentas.PRIVADO && (
                        <>
                          <FormGroups.ConductorFormGroup
                            fIsSubmitting={isSubmitting}
                            iError={errors._idConductor}
                            iTouched={touched._idConductor}
                          />
                          <FormGroups.PlacaFormGroup
                            fIsSubmitting={isSubmitting}
                            iError={errors._idPlaca}
                            iTouched={touched._idPlaca}
                          />
                        </>
                      )}
                      {values.modalidad === ModalidadGuiasVentas.PUBLICO && (
                        <>
                          <FormGroups.TransportistaFormGroup
                            iError={errors._idTransportista}
                            iTouched={touched._idTransportista}
                            fIsSubmitting={isSubmitting}
                          />
                        </>
                      )}
                    </Col>
                  </Row>
                  <Divider />
                  <Sections.DetallesSection detalles={detalles} dispatch={detallesDispatcher} />
                  <Divider />
                  <Row>
                    <Col md={8} />
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label text='TOTAL' md={8} alignRightAt='md' />
                        <Col md={4}>
                          <Form.InputNumber
                            id='text-sumaTotal'
                            name='text-sumaTotal'
                            disabled
                            placeholder={sumaDetalles}
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12} className='text-right'>
                      <Button disabled={isSubmitting} onClick={handleSubmit}>
                        Guardar
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </>
            )}
          </Formik>
        </Col>
      </Row>
    </>
  )
}

export default NuevaMainForm
